import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  Image,
  ImageSourcePropType,
  Platform,
  ScrollView,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Modal,
  TextInput,
  Animated,
} from 'react-native';
import Clipboard from '@react-native-clipboard/clipboard';
import TabBar from '../../components/TabBar';
import SimpleHeader from '../../components/SimpleHeader';
import PINDots from '../../components/UIComponents/PINDots';
import PinKeypad, { PIN_INPUT_PROPS } from '../../../web/PinKeypad';
import { COLORS } from '../../theme/themeTokens';
import { v2GetCard, v2GetCardOtpCode, v2GetCardNumber, v2GetCardCVV, v2GetCardPIN } from '../../services/v2/api';
import { usePIN } from '../../contexts/PINContext';
import { hasBiometricCredentials, authenticateWithBiometrics } from '../../services/biometrics';

// ──────────────────────────────────────────────────
// Props
// ──────────────────────────────────────────────────

interface CardInfoScreenProps {
  /** Payoma card id (== balance.wallet_address). Drives OTP / sensitive-
   *  card-data fetches. Пустая строка если parent не смог resolve'нуть —
   *  screen тогда просто скипает fetch и покажет секции пустыми. */
  cardId: string;
  /** Card block-state источник — cardDetail.data.blocked. Приходит из
   *  App-level state (setCardDetail на mount CardScreen'а). Используем
   *  для label'а кнопки внизу ("Block" vs "Unblock the card"). */
  isBlocked: boolean;
  onBack: () => void;
  onNavigate: (screen: string) => void;
  /** Открывает CardBlockScreen с нужным mode. Раньше Block уходил на
   *  onNavigate('cardBlock'), Unblock — в support-chat через prefill;
   *  теперь оба через один parent handler, чтобы UX + endpoint call
   *  жили в одном месте. */
  onBlockRequest: (mode: 'block' | 'unblock') => void;
}

// ──────────────────────────────────────────────────
// Static data — Commissions/limits (hardcoded per design)
// ──────────────────────────────────────────────────

const TABLE_ROWS: { label: string; value: string; icon: ImageSourcePropType }[] = [
  { label: 'Top-up fee', value: '2%', icon: require('../../assets/percent.png') },
  { label: 'Cash withdrawal', value: '2%', icon: require('../../assets/money.png') },
  { label: 'Limit per transaction', value: '50 000 €', icon: require('../../assets/receipt.png') },
  { label: 'Monthly limit on the card', value: '1M €', icon: require('../../assets/creditcard_white.png') },
  { label: 'Monthly limit on cash withdrawal transactions', value: '3 000 €', icon: require('../../assets/wallet.png') },
];

// ──────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────
//
// Экран "Card details" (title сменился с "Card info"). Собран из
/** How soon a failed one-time-code request is tried again. */
const OTP_RETRY_MS = 5000;

// секций перенесённых из CardScreen'а: OTP + card sensitive fields +
// Block/Unblock action + существующий Commissions and limits.
// CardScreen теперь только hero + Top-up + More-info + history.

const CardInfoScreen: React.FC<CardInfoScreenProps> = ({ cardId, isBlocked, onBack, onNavigate, onBlockRequest }) => {
  const statusBarHeight = Platform.OS === 'android' ? 28 : 0;

  // ── OTP ─────────────────────────────────────────
  const [otpCode, setOtpCode] = useState<string | null>(null);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(30);
  const otpTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Bumped on every code the server hands out, the same one included: the
  // countdown restarts on each answer, not only when the digits change. Keyed
  // on the code alone, a repeated code left it stuck on "0 sec" for good.
  const [otpRound, setOtpRound] = useState(0);
  const otpRetryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (otpRetryRef.current) clearTimeout(otpRetryRef.current);
    };
  }, []);

  // ── Sensitive card fields ───────────────────────
  const [cardNumber, setCardNumber] = useState<string | null>(null);
  const [cardCvv, setCardCvv] = useState<string | null>(null);
  const [cardPin, setCardPin] = useState<string | null>(null);
  const [expiryDate, setExpiryDate] = useState<string | null>(null);
  const [cardDetailsLoading, setCardDetailsLoading] = useState(false);
  const [cardDetailsVisible, setCardDetailsVisible] = useState(false);

  // ── PIN entry modal (gate for reveal) ───────────
  const [pinModalVisible, setPinModalVisible] = useState(false);
  const [modalPIN, setModalPIN] = useState('');
  const [modalError, setModalError] = useState<string | null>(null);
  const modalInputRef = useRef<TextInput>(null);
  const shakeAnimation = useRef(new Animated.Value(0)).current;

  const { verifyPIN, isPINSetup } = usePIN();

  const shakeModal = () => {
    Animated.sequence([
      Animated.timing(shakeAnimation, { toValue: 10, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnimation, { toValue: -10, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnimation, { toValue: 10, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnimation, { toValue: -10, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnimation, { toValue: 0, duration: 50, useNativeDriver: true }),
    ]).start();
  };

  const handleEyePress = async () => {
    if (cardDetailsVisible) {
      setCardDetailsVisible(false);
      return;
    }
    if (!isPINSetup) {
      setCardDetailsVisible(true);
      return;
    }
    const biometricsEnabled = await hasBiometricCredentials();
    if (biometricsEnabled) {
      const authenticated = await authenticateWithBiometrics();
      if (authenticated) setCardDetailsVisible(true);
      return;
    }
    setModalPIN('');
    setModalError(null);
    setPinModalVisible(true);
  };

  const handleModalPINChange = (value: string) => {
    const digits = value.replace(/[^0-9]/g, '').slice(0, 4);
    setModalError(null);
    setModalPIN(digits);

    if (digits.length === 4) {
      setTimeout(async () => {
        const isValid = await verifyPIN(digits);
        if (isValid) {
          setPinModalVisible(false);
          setModalPIN('');
          setCardDetailsVisible(true);
        } else {
          shakeModal();
          setModalError('Wrong PIN. Try again.');
          setModalPIN('');
        }
      }, 200);
    }
  };

  const handleModalClose = () => {
    setPinModalVisible(false);
    setModalPIN('');
    setModalError(null);
  };

  // ── Fetch OTP + sensitive fields on mount ───────
  useEffect(() => {
    if (!cardId) return;
    let mounted = true;

    const fetchOtp = async () => {
      try {
        setOtpLoading(true);
        const res = await v2GetCardOtpCode(cardId);
        const code = res?.data?.code ?? null;
        if (mounted) {
          setOtpCode(code != null ? String(code) : null);
          setOtpRound((n) => n + 1);
        }
      } catch (err) {
        console.warn('[CardInfoScreen] v2GetCardOtpCode failed for', cardId, err);
      } finally {
        if (mounted) setOtpLoading(false);
      }
    };

    const fetchSensitive = async () => {
      try {
        setCardDetailsLoading(true);
        // v2GetCard — /card/:id, public metadata (masked PAN + expiry_date +
        // status). expiry_date живёт здесь, НЕ на sensitive /card/:id/number
        // (проверено через scripts/get_card_number.go: Payoma отдаёт
        // "06/2030" на public endpoint'e).
        const [cardRes, numberRes, cvvRes, pinRes] = await Promise.all([
          v2GetCard(cardId).catch(() => null),
          v2GetCardNumber(cardId).catch(() => null),
          v2GetCardCVV(cardId).catch(() => null),
          v2GetCardPIN(cardId).catch(() => null),
        ]);
        if (mounted) {
          setCardNumber(numberRes?.data?.number != null ? String(numberRes.data.number) : null);
          setCardCvv(cvvRes?.data?.cvv != null ? String(cvvRes.data.cvv) : null);
          setCardPin(pinRes?.data?.pin != null ? String(pinRes.data.pin) : null);
          const exp = cardRes?.data?.expiry_date;
          setExpiryDate(exp != null ? String(exp) : null);
        }
      } catch (err) {
        console.warn('[CardInfoScreen] fetchSensitive failed for', cardId, err);
      } finally {
        if (mounted) setCardDetailsLoading(false);
      }
    };

    fetchOtp();
    fetchSensitive();
    return () => {
      mounted = false;
    };
  }, [cardId]);

  const refreshOtp = useCallback(async () => {
    if (!cardId) return;
    try {
      setOtpLoading(true);
      const res = await v2GetCardOtpCode(cardId);
      const code = res?.data?.code ?? null;
      if (!mountedRef.current) return;
      setOtpCode(code != null ? String(code) : null);
      setOtpRound((n) => n + 1);
    } catch (err) {
      console.warn('[CardInfoScreen] refreshOtp failed for', cardId, err);
      // The countdown sits on 0 until a code comes back; ask again shortly
      // rather than leave it there.
      if (mountedRef.current) {
        otpRetryRef.current = setTimeout(() => { refreshOtp(); }, OTP_RETRY_MS);
      }
    } finally {
      if (mountedRef.current) setOtpLoading(false);
    }
  }, [cardId]);

  // OTP countdown 30→0, restarted on every answer. Auto-refresh на 0.
  useEffect(() => {
    if (otpTimerRef.current) clearInterval(otpTimerRef.current);
    if (otpCode == null) return;
    setOtpCountdown(30);
    otpTimerRef.current = setInterval(() => {
      setOtpCountdown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => {
      if (otpTimerRef.current) clearInterval(otpTimerRef.current);
    };
  }, [otpCode, otpRound]);

  // A new code is asked for once, when the countdown reaches 0 — keyed on the
  // countdown alone. Keyed also on the code (or the round), it ran again on
  // the answer itself, while the countdown still read 0, and asked twice.
  const otpCodeRef = useRef(otpCode);
  otpCodeRef.current = otpCode;
  const refreshOtpRef = useRef(refreshOtp);
  refreshOtpRef.current = refreshOtp;
  useEffect(() => {
    if (otpCountdown === 0 && otpCodeRef.current != null) refreshOtpRef.current();
  }, [otpCountdown]);

  const maskValue = (val: string | null): string => {
    if (!val) return '••••';
    return '•'.repeat(val.length);
  };

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />

      <View style={[styles.fixedHeader, { paddingTop: statusBarHeight }]}>
        <SimpleHeader title="Card details" onBack={onBack} backgroundVariant="primaryNavy" />
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* OTP */}
        {otpLoading ? (
          <ActivityIndicator size="small" color={COLORS.brandOrange} style={styles.loader} />
        ) : otpCode != null ? (
          <View style={styles.otpCard}>
            <View style={styles.otpHeaderRow}>
              <Text style={styles.detailsTitle}>OTP</Text>
              <Text style={styles.otpCountdownText}>{otpCountdown} sec</Text>
            </View>
            <View style={styles.otpValueFrame}>
              <Text style={styles.otpValueText}>{otpCode}</Text>
              <TouchableOpacity
                style={styles.frameCopyBtn}
                activeOpacity={0.7}
                onPress={() => {
                  if (otpCode) {
                    Clipboard.setString(otpCode);
                    Alert.alert('Copied', 'OTP code copied to clipboard');
                  }
                }}
              >
                <Image source={require('../../assets/copy.png')} style={styles.copyIcon} />
              </TouchableOpacity>
            </View>
          </View>
        ) : null}

        {/* Card Details (sensitive fields with eye toggle) */}
        {cardDetailsLoading ? (
          <ActivityIndicator size="small" color={COLORS.brandOrange} style={styles.loader} />
        ) : (
          <View style={styles.cardDetailsCard}>
            <View style={styles.detailsHeaderRow}>
              <Text style={styles.detailsTitle}>Card details</Text>
              <TouchableOpacity style={styles.eyeBtn} activeOpacity={0.7} onPress={handleEyePress}>
                <Image
                  source={
                    cardDetailsVisible
                      ? require('../../assets/eye.png')
                      : require('../../assets/eyeclose.png')
                  }
                  style={styles.eyeIcon}
                />
              </TouchableOpacity>
            </View>

            <TouchableOpacity
              style={styles.detailFieldFull}
              activeOpacity={0.7}
              onPress={() => {
                if (cardDetailsVisible && cardNumber) {
                  Clipboard.setString(cardNumber);
                  Alert.alert('Copied', 'Card number copied to clipboard');
                }
              }}
            >
              <Text style={styles.detailFieldLabel}>Card Number</Text>
              <Text style={styles.detailFieldValue}>
                {cardDetailsVisible ? cardNumber ?? '' : maskValue(cardNumber)}
              </Text>
            </TouchableOpacity>

            <View style={styles.detailFieldRow}>
              <TouchableOpacity
                style={styles.detailFieldHalf}
                activeOpacity={0.7}
                onPress={() => {
                  if (cardDetailsVisible && expiryDate) {
                    Clipboard.setString(expiryDate);
                    Alert.alert('Copied', 'Expire date copied to clipboard');
                  }
                }}
              >
                <Text style={styles.detailFieldLabel}>Expire Date</Text>
                <Text style={styles.detailFieldValue}>
                  {cardDetailsVisible ? expiryDate ?? '' : maskValue(expiryDate)}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.detailFieldHalf}
                activeOpacity={0.7}
                onPress={() => {
                  if (cardDetailsVisible && cardCvv) {
                    Clipboard.setString(cardCvv);
                    Alert.alert('Copied', 'CVV copied to clipboard');
                  }
                }}
              >
                <Text style={styles.detailFieldLabel}>CVV/CVC</Text>
                <Text style={styles.detailFieldValue}>
                  {cardDetailsVisible ? cardCvv ?? '' : maskValue(cardCvv)}
                </Text>
              </TouchableOpacity>
            </View>

            <TouchableOpacity
              style={styles.detailFieldFull}
              activeOpacity={0.7}
              onPress={() => {
                if (cardDetailsVisible && cardPin) {
                  Clipboard.setString(cardPin);
                  Alert.alert('Copied', 'PIN copied to clipboard');
                }
              }}
            >
              <Text style={styles.detailFieldLabel}>Pin</Text>
              <Text style={styles.detailFieldValue}>
                {cardDetailsVisible ? cardPin ?? '' : maskValue(cardPin)}
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Block / Unblock the card — оба flow'а идут через onBlockRequest,
            которая переключает parent в 'cardBlock' tab с нужным mode.
            Раньше Unblock уходил в support-prefill; теперь прямой API call
            (v2UnblockCard) с PIN-подтверждением на том же экране. */}
        <TouchableOpacity
          style={styles.blockCardBtn}
          activeOpacity={0.7}
          onPress={() => onBlockRequest(isBlocked ? 'unblock' : 'block')}
        >
          <Image source={require('../../assets/lock_white.png')} style={styles.blockCardIcon} />
          <Text style={styles.blockCardBtnText}>
            {isBlocked ? 'Unblock the card' : 'Block the card'}
          </Text>
        </TouchableOpacity>

        {/* Commissions and limits — pureLight sheet */}
        <View style={styles.commissionsSheet}>
          <Text style={styles.heading}>Commissions and limits</Text>
          {TABLE_ROWS.map((row, idx) => (
            <View
              key={idx}
              style={[styles.tableRow, idx < TABLE_ROWS.length - 1 && styles.tableRowBorder]}
            >
              <Image source={row.icon} style={styles.rowIcon} />
              <Text style={styles.cellLabel}>{row.label}</Text>
              <Text style={styles.cellValue}>{row.value}</Text>
            </View>
          ))}
        </View>
      </ScrollView>

      <TabBar activeTab="dashboard" onNavigate={onNavigate} />

      {/* PIN Entry Modal */}
      <Modal
        visible={pinModalVisible}
        transparent
        animationType="fade"
        onRequestClose={handleModalClose}
      >
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={handleModalClose}>
          <View style={styles.pinModalContainer}>
            <Text style={styles.pinModalTitle}>Enter PIN</Text>
            <Text style={styles.pinModalSubtitle}>Enter your PIN to view card details</Text>

            <TextInput
              ref={modalInputRef}
              value={modalPIN}
              onChangeText={handleModalPINChange}
              keyboardType="number-pad"
              maxLength={4}
              secureTextEntry
              autoFocus
              style={styles.pinHiddenInput}
              caretHidden
              {...PIN_INPUT_PROPS}
            />

            <PINDots
              pin={modalPIN}
              pinLength={4}
              error={modalError}
              shakeAnimation={shakeAnimation}
              onPress={() => {
                modalInputRef.current?.blur();
                setTimeout(() => modalInputRef.current?.focus(), 50);
              }}
            />

            {modalError && <Text style={styles.pinModalError}>{modalError}</Text>}

            <PinKeypad value={modalPIN} length={4} onChange={handleModalPINChange} compact />

            <TouchableOpacity style={styles.pinModalCancelButton} onPress={handleModalClose}>
              <Text style={styles.pinModalCancelText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

// ──────────────────────────────────────────────────
// Styles
// ──────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.primaryNavy,
  },
  fixedHeader: {
    backgroundColor: COLORS.primaryNavy,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 140,
  },
  loader: {
    marginVertical: 20,
  },

  // ── OTP + Card details cards (navy on navy strip) ────────
  otpCard: {
    width: '100%',
    borderRadius: 8,
    marginBottom: 12,
    overflow: 'hidden',
    backgroundColor: COLORS.steelNavy,
    padding: 16,
  },
  cardDetailsCard: {
    width: '100%',
    borderRadius: 8,
    marginBottom: 12,
    overflow: 'hidden',
    backgroundColor: COLORS.steelNavy,
    padding: 16,
  },
  detailsTitle: {
    fontSize: 18,
    fontWeight: '500',
    color: COLORS.white,
  },
  detailsHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: 8,
  },
  eyeBtn: {
    padding: 4,
  },
  eyeIcon: {
    width: 24,
    height: 24,
    tintColor: COLORS.blueSteel,
  },
  detailFieldFull: {
    width: '100%',
    backgroundColor: COLORS.white,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 8,
  },
  detailFieldRow: {
    flexDirection: 'row',
    width: '100%',
    gap: 8,
    marginBottom: 8,
  },
  detailFieldHalf: {
    flex: 1,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  detailFieldLabel: {
    fontSize: 14,
    color: COLORS.blueSteel,
    fontWeight: '400',
    marginBottom: 4,
  },
  detailFieldValue: {
    fontSize: 18,
    color: COLORS.textPrimary,
    fontWeight: '600',
  },
  frameCopyBtn: {
    padding: 4,
  },
  copyIcon: {
    width: 20,
    height: 20,
    tintColor: COLORS.blueSteel,
  },
  otpHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: 0,
  },
  otpCountdownText: {
    fontSize: 16,
    color: COLORS.blueSteel,
    fontWeight: '400',
    marginRight: 8,
  },
  otpValueFrame: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    backgroundColor: COLORS.white,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginTop: 8,
  },
  otpValueText: {
    fontSize: 18,
    color: COLORS.textPrimary,
    fontWeight: '500',
  },

  // ── Block/Unblock button ─────────────────────────────────
  blockCardBtn: {
    width: '100%',
    backgroundColor: COLORS.steelNavy,
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginBottom: 20,
  },
  blockCardIcon: {
    width: 20,
    height: 20,
    marginRight: 8,
    tintColor: COLORS.white,
  },
  blockCardBtnText: {
    fontFamily: 'Inter',
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '500',
    color: COLORS.white,
  },

  // ── Commissions and limits (pureLight sheet) ────────────
  commissionsSheet: {
    backgroundColor: COLORS.pureLight,
    borderRadius: 12,
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 12,
  },
  heading: {
    fontSize: 22,
    lineHeight: 32,
    fontWeight: '500',
    color: COLORS.primaryNavy,
    textAlign: 'left',
    marginBottom: 20,
  },
  tableRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
  },
  tableRowBorder: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#E5E7EB',
  },
  rowIcon: {
    width: 22,
    height: 22,
    marginRight: 10,
    tintColor: COLORS.steelNavy,
  },
  cellLabel: {
    flex: 1,
    fontSize: 18,
    lineHeight: 22,
    color: COLORS.primaryNavy,
    fontWeight: '400',
    paddingRight: 12,
  },
  cellValue: {
    fontSize: 16,
    lineHeight: 22,
    color: COLORS.brandOrange,
    fontWeight: '500',
    textAlign: 'right',
  },

  // ── PIN Modal ────────────────────────────────────────────
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  pinModalContainer: {
    backgroundColor: COLORS.pureLight,
    borderRadius: 16,
    padding: 24,
    width: '85%',
    alignItems: 'center',
  },
  pinModalTitle: {
    fontFamily: 'Inter',
    fontSize: 20,
    fontWeight: '600',
    color: COLORS.deepNavy,
    marginBottom: 8,
  },
  pinModalSubtitle: {
    fontFamily: 'Inter',
    fontSize: 16,
    color: COLORS.blueSteel,
    marginBottom: 24,
  },
  pinModalError: {
    fontFamily: 'Inter',
    fontSize: 15,
    color: '#E53935',
    marginTop: -8,
    marginBottom: 8,
  },
  pinModalCancelButton: {
    marginTop: 8,
    paddingVertical: 10,
    paddingHorizontal: 24,
  },
  pinModalCancelText: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    color: COLORS.blueSteel,
  },
  pinHiddenInput: {
    position: 'absolute',
    opacity: 0,
    height: 1,
    width: 1,
  },
});

export default CardInfoScreen;
